import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandS, BrandXXL } from '@sorare/core/src/atoms/typography';
import { HideSeriesBottomNav } from '@sorare/core/src/components/navigation/AppNavigation/HideSeriesBottomNav';
import { ANY_SPORT_PLAY_SQUAD_CREATE_OR_JOIN } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import squad from '#src/assets/squad.webp';
import { SquadBackground } from '#src/components/squad/SquadBackground/index.tsx';

import cardPackLeft from './assets/card-pack-left.webp';
import cardPackRight from './assets/card-pack-right.webp';
import field from './assets/field.webp';

const CORNER_VIEWBOX = '0 0 35.4751 34.689';
const CORNER_PATH =
  'M8.76205 0L0 8.76205V34.689L5.24046 31.1179V18.9266L4.59541 18.496V10.1293L10.0179 4.70689H18.3788L18.8971 5.24046H31.9487L35.4751 0H8.76205Z';

const Root = styled(Vertical)`
  position: relative;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  overflow: hidden;
  align-items: center;
`;

const CornerSvg = styled.svg`
  position: absolute;
  width: 35px;
  height: 35px;

  &.top-left {
    top: var(--quadruple-unit);
    left: var(--double-unit);
  }
  &.top-right {
    top: var(--quadruple-unit);
    right: var(--double-unit);
    transform: rotate(90deg);
  }
  &.bottom-right {
    bottom: var(--double-unit);
    right: var(--double-unit);
    transform: rotate(180deg);
  }
  &.bottom-left {
    bottom: var(--double-unit);
    left: var(--double-unit);
    transform: rotate(270deg);
  }
`;

const Content = styled(Vertical)`
  position: relative;
  z-index: 1;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--quadruple-unit);
  width: 100%;
`;

const TitleWrapper = styled(Horizontal)`
  align-items: center;
  justify-content: center;
`;

const Medal = styled.img`
  width: 48px;
  height: auto;
`;

const CardPacksArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 350px;
  aspect-ratio: 350 / 260;
`;

const Field = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const CardPack = styled.img`
  position: absolute;
  height: 85%;
  width: auto;
  top: 0;
  left: 50%;

  &.left {
    transform: translateX(-70%) rotate(-15deg);
  }
  &.right {
    transform: translateX(-30%) rotate(15deg);
  }
`;

const TITLE_GRADIENT = 'linear-gradient(180deg, #aa83ff 18%, #7d41ff 100%)';

const CORNER_POSITIONS = [
  'top-left',
  'top-right',
  'bottom-right',
  'bottom-left',
] as const;

export const SquadWelcomeDialog = () => {
  const { generateSportContextPath } = useSportContext();

  return (
    <Root>
      <SquadBackground />
      {CORNER_POSITIONS.map(position => (
        <CornerSvg
          key={position}
          className={position}
          viewBox={CORNER_VIEWBOX}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={CORNER_PATH} fill="#0DCB02" />
        </CornerSvg>
      ))}
      <Content gap={3}>
        <Horizontal gap={0.5} center>
          <SorareLogo size="small" />
          <BrandS>
            <FormattedMessage
              id="SquadWelcomeDialog.logoLabel"
              defaultMessage="Assemble Squad"
            />
          </BrandS>
        </Horizontal>
        <Vertical gap={1} center>
          <TitleWrapper gap={0.5}>
            <Medal src={squad} alt="" />
            <BrandXXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': TITLE_GRADIENT,
                } as CSSProperties
              }
            >
              <FormattedMessage
                id="SquadWelcomeDialog.title"
                defaultMessage="Squads"
              />
            </BrandXXL>
          </TitleWrapper>
          <BodyL color="var(--c-white)" className="text-center">
            <FormattedMessage
              id="SquadWelcomeDialog.description"
              defaultMessage="Earn exclusive packs by playing with your friends"
            />
          </BodyL>
        </Vertical>
        <CardPacksArea>
          <Field src={field} alt="" />
          <CardPack
            className="left"
            src={cardPackLeft}
            alt=""
            style={{ zIndex: 2 }}
          />
          <CardPack className="right" src={cardPackRight} alt="" />
        </CardPacksArea>
        <Button
          size="large"
          color="primary"
          to={generateSportContextPath(ANY_SPORT_PLAY_SQUAD_CREATE_OR_JOIN)}
        >
          <FormattedMessage
            id="SquadWelcomeDialog.cta"
            defaultMessage="Let's go!"
          />
        </Button>
      </Content>
      <HideSeriesBottomNav />
    </Root>
  );
};
